<script setup lang="ts">
import { computed } from 'vue'

import type { InsightDraftResponse } from '../../../models/insightAuthoring.types'
import {
  formatDiffValue,
  summarizeDiff,
  type AuthoringPreviewSection,
} from '../../../service/insightAuthoringDiff'

import { useI18n } from '@/shared/i18n'

const props = defineProps<{
  draft: InsightDraftResponse['draft'] | null
  diff: InsightDraftResponse['diff']
}>()
const { t } = useI18n()
const summary = computed(() => summarizeDiff(props.diff))
const sections: readonly AuthoringPreviewSection[] = [
  'overview',
  'classification',
  'detection',
  'content',
  'impact',
  'pipeline',
  'visualization',
]
</script>

<template>
  <section class="authoring-diff grid gap-md" data-testid="authoring-draft-diff">
    <h2>{{ t('insights.sysadmin.authoring.diff.title') }}</h2>
    <p v-if="!draft">{{ t('insights.sysadmin.authoring.diff.empty') }}</p>
    <div v-else class="authoring-diff__sections grid gap-md">
      <section
        v-for="section in sections"
        :key="section"
        class="authoring-diff__section p-md border border-border rounded-md bg-surface"
      >
        <h3>{{ t(`insights.sysadmin.config.section.${section}`) }}</h3>
        <ul v-if="summary.bySection[section]?.length">
          <li
            v-for="entry in summary.bySection[section]"
            :key="`${entry.path}:${entry.op}`"
            :class="`authoring-diff__entry authoring-diff__entry--${entry.op}`"
          >
            <code>{{ entry.path }}</code>
            <span>{{ formatDiffValue(entry.old) }}</span>
            <span>{{ t('insights.sysadmin.authoring.diff.to') }}</span>
            <span>{{ formatDiffValue(entry.new) }}</span>
          </li>
        </ul>
        <p v-else>{{ t('insights.sysadmin.authoring.diff.unchanged') }}</p>
      </section>
    </div>
  </section>
</template>

<style scoped>
.authoring-diff .authoring-diff__section h3 {
  margin: 0;
}
.authoring-diff .authoring-diff__section p {
  margin: 0;
}
.authoring-diff .authoring-diff__entry {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) 1fr auto 1fr;
  gap: var(--ui-space-sm);
  padding-block: var(--ui-space-xs);
}
.authoring-diff :deep(.authoring-diff__entry--add) {
  color: var(--ui-color-success);
}
.authoring-diff :deep(.authoring-diff__entry--remove) {
  color: var(--ui-color-danger);
}
</style>
